// The contract module for vendor board returns.
//
// Every assumption this feature makes about the API's shape lives here, read off
// api/src/procurement/board-returns-router.js and -service.js. Same convention as
// poApi.ts, poImportApi.ts and quoteApi.ts: pages import types and callers from
// here and never hand-build a path or guess a field name.
//
// ── ONE RULE THIS MODULE EXISTS TO ENFORCE ───────────────────────────────────
// The frontend NEVER computes a label identity. The server owns that string, and
// it is stored on the line precisely so the sticker and the slip cannot disagree.
// A second implementation over here is how they drift, so there is no such
// function in this folder: both the label printer and the return slip read
// `line.label_identity`.

import { useApi } from '../../apiHelpers';

const BASE = '/procurement/board-returns';

// ─────────────────────────────────────────────────────────────────────────────
// Vocabulary
// ─────────────────────────────────────────────────────────────────────────────

/** Kept in step with BoardReturnsService.MODEL_SERIALS. Both instances, one list. */
export const MODEL_SERIALS = ['F020', 'F021', 'D021'] as const;
export type ModelSerial = (typeof MODEL_SERIALS)[number];

/** Mirrors boards_board_type_check. A backend spec asserts the two agree. */
export const BOARD_TYPES = ['AP', 'ESC', 'BAT', 'CHG', 'LED', 'GPS', 'CM5'] as const;
export type BoardType = (typeof BOARD_TYPES)[number];

export type ManifestStatus = 'draft' | 'sent' | 'closed' | 'cancelled';

/** Mirrors purchase_returns_status_check. `credited` was retired in 071: it said
 *  the money came back without saying whether stock or a refund settled it. */
export const PURCHASE_RETURN_STATUSES = [
  'open',
  'shipped',
  'replaced',
  'refunded',
  'scrapped',
  'cancelled',
] as const;
export type PurchaseReturnStatus = (typeof PURCHASE_RETURN_STATUSES)[number];

/**
 * What a return's status is CALLED, in one place.
 *
 * `shipped` reads as "Awaiting resolution" rather than "Sent back" because the
 * status is not about the parcel: the box left, and the only thing anybody is
 * waiting on now is the vendor's answer. Two pages show these, so the words live
 * here rather than being typed twice and drifting.
 */
export const RETURN_STATUS_LABELS: Record<PurchaseReturnStatus, string> = {
  open: 'Open',
  shipped: 'Awaiting resolution',
  replaced: 'Replaced',
  refunded: 'Refunded',
  scrapped: 'Scrapped',
  cancelled: 'Cancelled',
};

/** The label, or the raw value for a status this build has not heard of. */
export function returnStatusLabel(status: string | null | undefined): string {
  if (!status) return '';
  return RETURN_STATUS_LABELS[status as PurchaseReturnStatus] || status;
}

export interface ReturnReason {
  id: number;
  code: string;
  label: string;
  description: string | null;
  origin: 'preset' | 'ai' | 'human';
  is_active: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Lookup
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Why a lookup found nothing. A miss arrives as HTTP 200, because on this page
 * "no record" is an answer and which flavour it is decides what the tech does
 * next.
 *
 *   board_not_found      the code resolved to nothing at all
 *   uid_ambiguous        a typed suffix matched more than one board. Common for
 *                        ESC, whose 24-char UIDs share a wafer tail
 *   drone_not_found      no drone with that model and last4
 *   board_not_registered THE COMMON ONE. The drone is real, that board type was
 *                        never registered against it. 4 of 1756 ESC boards carry
 *                        a drone serial, so this is the majority outcome
 */
export type LookupMiss =
  | 'board_not_found'
  | 'uid_ambiguous'
  | 'drone_not_found'
  | 'board_not_registered';

export interface LookupBoard {
  id: string;
  board_type: BoardType;
  primary_uid: string;
  secondary_uid: string | null;
  board_code: string | null;
  serialnumber: string | null;
  batch_number: string | null;
  batch_receive_date: string | null;
  is_current: boolean;
  retired_at: string | null;
  created_at: string;
  import_source: string | null;
}

export interface LookupFlash {
  operation_timestamp: string;
  station_id: string;
  firmware_file: string;
  flash_type: string;
  success: boolean;
  error_message: string | null;
  duration_seconds: string | null;
}

export interface LookupBox {
  barcode: string;
  /** Free text on the box. The only vendor most boards have on record. */
  vendor: string | null;
  vendor_id: string | null;
  vendor_name: string | null;
  lot_number: string | null;
  mpn: string | null;
  /** Free text, e.g. "PO-SDTEC-26012". Not a link to purchase_orders. */
  purchase_order_ref: string | null;
  received_at: string | null;
  sku: string | null;
  description: string | null;
}

export interface LookupHit {
  found: true;
  kind: 'board';
  board: LookupBoard;
  drone: { serialnumber: string; title: string | null; condition: string | null } | null;
  box: LookupBox | null;
  /**
   * The drone serial when the board has one, otherwise NULL — and null is a real
   * answer meaning "a manifest line tag gets assigned when you add it", not
   * "unknown". No line number exists before the line does.
   */
  label_identity_preview: string | null;
  latest_flash: LookupFlash | null;
  recent_flashes: LookupFlash[];
  failed_flash_count: number;
  /** Registered but never programmed, so "failed flash" would be a lie. */
  no_flash_history: boolean;
  suggested_reason: Pick<ReturnReason, 'id' | 'code' | 'label'> | null;
  /** The station's own first line, capped. Not the full console capture. */
  suggested_reason_raw: string | null;
  already_on_manifest: {
    manifest_id: string;
    manifest_number: string;
    status: ManifestStatus;
    line_number: number;
  } | null;
  /** Set once a send retired the board. The entry bar says so and adds nothing. */
  retired: boolean;
}

export type PartInstanceStatus = 'in_stock' | 'installed' | 'retired' | 'lost';

export const PART_INSTANCE_STATUS_LABELS: Record<PartInstanceStatus, string> = {
  in_stock: 'In stock',
  installed: 'Installed',
  retired: 'Retired',
  lost: 'Lost',
};

/**
 * A sticker-tracked unit: a purchased PCBA (GPS, LED) with no chip UID, so it
 * never enters `boards`. Its sticker encodes this id, dash-stripped.
 */
export interface LookupPartInstance {
  id: string;
  sku: string | null;
  base_name: string | null;
  variant_detail: string | null;
  display_name: string | null;
  status: PartInstanceStatus;
  /** A drone serial, when the unit is fitted to one. */
  installed_on: string | null;
  installed_at: string | null;
  retired_at: string | null;
  receipt_item_id: string | null;
  created_at: string;
  /** Read off the SKU by the server. Null for a SKU that names no board kind. */
  board_type: 'GPS' | 'LED' | 'PYR' | null;
}

export interface LookupPartInstanceHit {
  found: true;
  kind: 'part_instance';
  instance: LookupPartInstance;
  drone: {
    serialnumber: string;
    title: string | null;
    condition: string | null;
    generation: string | null;
  } | null;
  box: LookupBox | null;
  /** Always null: a unit gets its -P tag when the line exists. */
  label_identity_preview: null;
  suggested_reason: null;
  suggested_reason_raw: null;
  /** No line_number here, unlike a board's. */
  already_on_manifest: { manifest_id: string; manifest_number: string } | null;
  /** Nothing is added for a retired unit; the entry bar says so in place. */
  retired: boolean;
}

export interface LookupMissResult {
  found: false;
  miss: LookupMiss;
  drone?: { serialnumber: string; title: string | null };
  board_type?: BoardType;
}

/** Narrow on `found` first: a miss carries no `kind`. */
export type LookupResult = LookupHit | LookupPartInstanceHit | LookupMissResult;

// ─────────────────────────────────────────────────────────────────────────────
// Manifests and lines
// ─────────────────────────────────────────────────────────────────────────────

export interface ManifestRow {
  id: string;
  manifest_number: string;
  vendor_id: string | null;
  vendor_name: string | null;
  rma_number: string | null;
  status: ManifestStatus;
  notes: string | null;
  sent_at: string | null;
  created_at: string;
  updated_at: string;
  created_by_firstname: string | null;
  created_by_lastname: string | null;
  line_count: number;
}

export interface ManifestDetail extends Omit<ManifestRow, 'line_count'> {
  vendor_code: string | null;
  vendor_contact_name: string | null;
  vendor_contact_email: string | null;
  vendor_address_line1: string | null;
  vendor_address_line2: string | null;
  vendor_city: string | null;
  vendor_region: string | null;
  vendor_postal_code: string | null;
  vendor_country: string | null;
}

export interface ManifestLine {
  id: string;
  manifest_id: string;
  line_number: number;
  /** Set for a board with a record. Mutually exclusive with doa_number. */
  board_id: string | null;
  /** Set for a board the system never saw. Mutually exclusive with board_id. */
  doa_number: string | null;
  /**
   * Set for a QUANTITY of loose stock going back, raised off a purchase order
   * rather than scanned off a board. The third identity, and the DB knows it as
   * one: board_return_lines_identity_check allows exactly one of board_id,
   * doa_number and part_catalog_id to be set.
   */
  part_catalog_id: number | null;
  /**
   * Set for one sticker-tracked unit (GPS, LED, PYRO), migration 118. The
   * fourth identity in board_return_lines_identity_check.
   */
  part_instance_id: string | null;
  /** How many units. NULL on every kind but a quantity line, the only one that
   *  is not a single unit. */
  quantity: number | null;
  /** Stored on DOA lines only; read effective_* instead. */
  board_type: BoardType | null;
  model_serial: ModelSerial | null;
  /** WHAT WENT ON THE STICKER. Never recompute this. */
  label_identity: string;
  reason_id: number | null;
  reason_raw: string | null;
  normalized_at: string | null;
  designator: string | null;
  in_print_list: boolean;
  purchase_return_id: string | null;
  created_at: string;

  // Resolved by the service, so a board line and a DOA line read the same way.
  effective_board_type: BoardType | null;
  effective_model_serial: string | null;
  primary_uid: string | null;
  secondary_uid: string | null;
  serialnumber: string | null;
  batch_number: string | null;
  board_code: string | null;
  is_current: boolean | null;
  reason_code: string | null;
  reason_label: string | null;
  reason_origin: ReturnReason['origin'] | null;
  drone_title: string | null;

  // Quantity and part instance lines, joined by the service. The name fields come from
  // catalog_item_names, the ONE definition of how an item is written down, so a
  // manifest row and a catalog row cannot spell the same item differently.
  sku: string | null;
  base_name: string | null;
  variant_detail: string | null;
  display_name: string | null;
  /** The receipt box the line came out of: the claim's box for stock, the
   *  board's own for a board. Our handle, not the vendor's. */
  box_barcode: string | null;
  /** The purchase order that box arrived on, which is what the vendor's desk
   *  files a return under. Resolved from the order, not from text on the box. */
  po_number: string | null;
  /** That order was placed with a different vendor than this manifest is going
   *  to. The slip leaves the number off; it is the other vendor's business. */
  po_is_other_vendor: boolean;
  /** The linked purchase_returns row's status, so the manifest can show whether
   *  the money claim behind a line is still live. */
  return_status: PurchaseReturnStatus | null;

  // Part instance lines only. The name fields above, effective_board_type (from
  // the SKU), drone_title, box_barcode and po_number are filled for them too.
  instance_status: PartInstanceStatus | null;
  /** The drone serial the unit is fitted to, when it is. */
  instance_installed_on: string | null;
  instance_retired_at: string | null;
}

export interface NormalizeProposal {
  line_id: string;
  line_number: number;
  label_identity: string;
  reason_raw: string;
  code: string;
  is_new: boolean;
  label: string | null;
  description: string | null;
  confidence: number | null;
  note: string | null;
}

export interface NormalizeResult {
  ok: true;
  proposals: NormalizeProposal[];
  new_reasons: { code: string; label: string; description: string | null }[];
  /** Lines the model answered for that we did not ask about, and why they were dropped. */
  skipped: { n: number | undefined; why: string; code?: string }[];
  pending_count: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Callers
// ─────────────────────────────────────────────────────────────────────────────

/** The lean vendor projection, open to any signed-in user. */
export interface VendorOption {
  id: string;
  /** Two letters, or null. The picker shows it as a chip: it is the same code
   * the board's SKU carries, so it is what a tech types to find the vendor. */
  code: string | null;
  name: string;
}

export function useBoardReturnsApi() {
  const api = useApi();

  return {
    /**
     * The vendor picker's options.
     *
     * Lives here rather than in the page because the response shape is the thing
     * this module exists to be right about: GET /procurement/vendors answers
     * `{ vendors, types }`, NOT `{ rows }` like every other list endpoint on this
     * surface. Assuming `rows` crashed the manifest page on first load.
     *
     * The Array.isArray guard is not defensive padding: the vendor picker is
     * optional to this page (a vendor is required to SEND, not to scan boards in),
     * so an unexpected shape has to degrade to an empty select rather than take
     * the page down with it.
     */
    listVendors: async (): Promise<VendorOption[]> => {
      const data = await api.get('/procurement/vendors');
      const list = (data as { vendors?: unknown })?.vendors;
      return Array.isArray(list) ? (list as VendorOption[]) : [];
    },

    listReasons: (): Promise<{ rows: ReturnReason[] }> => api.get(`${BASE}/reasons`),

    lookup: (body: {
      uid?: string;
      model_serial?: string;
      board_type?: string;
      last4?: string;
    }): Promise<LookupResult> => api.post(`${BASE}/lookup`, { body }),

    /** `vendorId` narrows the list to one vendor's manifests, which is what the
     *  PO page's manifest picker needs: a draft belonging to another vendor
     *  cannot take these lines and offering it is offering an error. Note that
     *  it also excludes drafts with NO vendor yet, which the server does accept
     *  and adopts on first use. */
    listManifests: (status?: string, vendorId?: string): Promise<{ rows: ManifestRow[] }> => {
      const qs = new URLSearchParams();
      if (status) qs.set('status', status);
      if (vendorId) qs.set('vendor_id', vendorId);
      const suffix = qs.toString();
      return api.get(`${BASE}${suffix ? `?${suffix}` : ''}`);
    },

    createManifest: (body: {
      vendor_id?: string | null;
      rma_number?: string;
      notes?: string;
    }): Promise<{ ok: true; manifest: ManifestRow }> => api.post(BASE, { body }),

    getManifest: (
      id: string,
    ): Promise<{ ok: true; manifest: ManifestDetail; lines: ManifestLine[] }> =>
      api.get(`${BASE}/${id}`),

    updateManifest: (
      id: string,
      body: {
        vendor_id?: string | null;
        rma_number?: string | null;
        notes?: string | null;
        status?: ManifestStatus;
      },
    ): Promise<{ ok: true; manifest: ManifestDetail }> => api.patch(`${BASE}/${id}`, { body }),

    addBoardLine: (
      manifestId: string,
      body: {
        board_id: string;
        reason_id?: number | null;
        reason_code?: string;
        reason_raw?: string | null;
        designator?: string | null;
        in_print_list?: boolean;
      },
    ): Promise<{ ok: true; line: ManifestLine }> =>
      api.post(`${BASE}/${manifestId}/lines`, { body }),

    addPartInstanceLine: (
      manifestId: string,
      body: {
        part_instance_id: string;
        reason_id?: number | null;
        reason_raw?: string | null;
        designator?: string | null;
        in_print_list?: boolean;
      },
    ): Promise<{ ok: true; line: ManifestLine }> =>
      api.post(`${BASE}/${manifestId}/lines/part-instance`, { body }),

    addDoaLines: (
      manifestId: string,
      body: {
        count: number;
        board_type: string;
        model_serial?: string | null;
        reason_id?: number | null;
        reason_code?: string;
        reason_raw?: string | null;
        designator?: string | null;
        in_print_list?: boolean;
      },
    ): Promise<{ ok: true; lines: ManifestLine[] }> =>
      api.post(`${BASE}/${manifestId}/lines/doa`, { body }),

    updateLine: (
      manifestId: string,
      lineId: string,
      body: {
        reason_id?: number | null;
        reason_raw?: string | null;
        designator?: string | null;
        in_print_list?: boolean;
      },
    ): Promise<{ ok: true; line: ManifestLine }> =>
      api.patch(`${BASE}/${manifestId}/lines/${lineId}`, { body }),

    deleteLine: (manifestId: string, lineId: string): Promise<void> =>
      api.delete(`${BASE}/${manifestId}/lines/${lineId}`),

    /** Asks the model. Writes nothing. */
    normalize: (manifestId: string): Promise<NormalizeResult> =>
      api.post(`${BASE}/${manifestId}/normalize`, { body: {} }),

    /** Commits what a human accepted, minting new codes with origin 'ai'. */
    applyNormalization: (
      manifestId: string,
      proposals: NormalizeProposal[],
    ): Promise<{
      ok: true;
      applied: number;
      refused: { line_id: string; why: string }[];
      minted: string[];
    }> => api.post(`${BASE}/${manifestId}/normalize/apply`, { body: { proposals } }),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared presentation helpers
// ─────────────────────────────────────────────────────────────────────────────

/** A DOA line is a board nothing in the system ever knew about. */
export function isDoaLine(line: ManifestLine): boolean {
  return line.doa_number !== null;
}

/**
 * A quantity line is loose stock going back by the count, not by the unit.
 *
 * Told apart by part_catalog_id rather than by `quantity`, because the identity
 * column is the one the database constrains: a line with a part is a stock line
 * even if something upstream ever sends the count separately.
 */
export function isQuantityLine(line: ManifestLine): boolean {
  return line.part_catalog_id !== null;
}

/** One sticker-tracked unit (GPS, LED, PYRO): a unit on record that never
 *  passed a flash station, so it is not in `boards`. */
export function isPartInstanceLine(line: ManifestLine): boolean {
  return line.part_instance_id != null;
}

/**
 * What the reason column shows.
 *
 * The code is the countable value and the label is the readable one, so the label
 * wins wherever there is room. A line with words but no code yet is exactly what
 * the normalization pass is for, and it says so rather than showing a blank.
 */
export function reasonDisplay(line: ManifestLine): string {
  if (line.reason_label) return line.reason_label;
  if (line.reason_raw) return line.reason_raw;
  return '';
}

export const MISS_COPY: Record<LookupMiss, string> = {
  board_not_found: 'No board on record with that code. Add it as a DOA board instead.',
  uid_ambiguous:
    'That partial code matches more than one board. Scan the QR, or type the whole UID.',
  drone_not_found: 'No drone with that model and last four.',
  board_not_registered: 'That drone is real, but this board type was never registered against it.',
};

// ─────────────────────────────────────────────────────────────────────────────
// Errors
// ─────────────────────────────────────────────────────────────────────────────

/**
 * What went wrong, in words. The server's router owns the copy for every code
 * (one home, so the page and the API cannot disagree); the one thing it cannot
 * say is WHICH manifest, because that is a field on the 409 rather than a
 * sentence, so that case is composed here.
 */
export function errorMessage(err: unknown, fallback: string): string {
  const e = err as { message?: string; data?: { error?: { code?: string; manifest_number?: string } } };
  const where = e?.data?.error?.manifest_number;
  if (e?.data?.error?.code === 'part_instance_already_on_manifest' && where) {
    return `That unit is already on ${where}.`;
  }
  return e?.message || fallback;
}
